import * as Actions from './actions'
import Action from './Action'
import { isOfType } from 'global'
import {
  IResponse,
  IQuestion,
  IResult,
  IProduct,
  IHand,
  IContentConfig,
} from 'models'

export type IScreenState = string

export interface IUiState {
  filigree: React.CSSProperties
  digestBg: React.CSSProperties
  contentConfig: Partial<IContentConfig>
}

export interface IQuestionsState {
  allQuestions: IQuestion[]
  filteredQuestions: IQuestion[]
  currentQuestionIndex: number
}

export interface IQuestionsFilterPayload {
  filterCategory: string[]
  filterRef: Pick<IQuestion, 'id'>
}

export type IResponsesState = IResponse[]

export type IProductsState = IProduct[]

// initial states
export const initialScreen: IScreenState = 'StartScreen'
export const initialUi: IUiState = {
  filigree: {},
  digestBg: {},
  contentConfig: {},
}
export const initialQuestions: IQuestionsState = {
  allQuestions: [], // store all questions fetched from server
  filteredQuestions: [], // the actual questions displayed in UI
  currentQuestionIndex: 0,
}
export const initialResponses: IResponsesState = []
export const initialProducts: IProductsState = []
export const initialResult: IResult = {} as IResult

export const uiReducer = (
  state: IUiState,
  action: Action<Partial<IUiState>>
) => {
  switch (action.type) {
    case Actions.ADD_UI_STATE:
      return {
        ...state,
        ...action.payload,
      }
    default:
      return state
  }
}

// reducers
export const screenReducer = (
  state: IScreenState,
  action: Action<undefined>
): IScreenState => {
  switch (action.type) {
    case Actions.GO_TO_START_SCREEN:
      return 'StartScreen'
    case Actions.GO_TO_QUESTIONS_SCREEN:
      return 'QuestionsScreen'
    case Actions.GO_TO_HAND_SCREEN:
      return 'HandScreen'
    case Actions.GO_TO_DIGEST_SCREEN:
      return 'DigestScreen'
    default:
      return state
  }
}

export const questionsReducer = (
  state: IQuestionsState,
  action: Action<IQuestion[] | IQuestionsFilterPayload>
): IQuestionsState => {
  switch (action.type) {
    case Actions.SET_QUESTIONS:
      if (action.payload && isOfType<IQuestion[]>(action.payload, 'map')) {
        const newState = {
          ...state,
          allQuestions: action.payload,
          filteredQuestions: action.payload,
        }
        return newState
      } else {
        return state
      }
    case Actions.FILTER_QUESTIONS:
      // filter format
      // {
      //  filterCategory: ['categoty 1', 'category 2'],
      //  filterRef: { id: 4 }
      // }
      if (
        action.payload &&
        isOfType<IQuestionsFilterPayload>(action.payload, 'filterRef')
      ) {
        const newFilter = action.payload

        const newFilteredQuestions = state.filteredQuestions.filter(
          (question) => {
            // senarios:
            // 1, questions without filterRef
            // 2, questions with filterRef but the category is in the filter
            if (question.filterRef) {
              // filter the question based on current filter
              const questionWithSameFilterRef =
                newFilter.filterRef.id === question.filterRef.id &&
                question.category &&
                newFilter.filterCategory.includes(question.category)
              const questionWithDiffRef =
                newFilter.filterRef.id !== question.filterRef.id

              return questionWithSameFilterRef || questionWithDiffRef
            } else {
              return true
            }
          }
        )

        return {
          ...state,
          filteredQuestions: newFilteredQuestions,
        }
      } else {
        return state
      }
    case Actions.GO_TO_NEXT_QUESTION:
      return {
        ...state,
        currentQuestionIndex: state.currentQuestionIndex + 1,
      }
    case Actions.GO_TO_PREV_QUESTION:
      const prevIndex =
        state.currentQuestionIndex > 0 ? state.currentQuestionIndex - 1 : 0
      return {
        ...state,
        currentQuestionIndex: prevIndex,
      }
    case Actions.RESET_QUESTIONS:
      return initialQuestions
    default:
      return state
  }
}

export const responsesReducer = (
  state: IResponsesState,
  action: Action<IResponse | IQuestion[]>
): IResponsesState => {
  switch (action.type) {
    case Actions.GENERATE_INITIAL_RESPONSES:
      if (action.payload && isOfType<IQuestion[]>(action.payload, 'map')) {
        // initial result format as below
        return action.payload.map((question) => ({
          question,
          answered: false,
          filtered: question.filterRef ? false : true, // only set the questions WITHOUT a filterRef to true, the reset should be determined when each question is answered
          answers: [],
        }))
      } else {
        return state
      }
    case Actions.ADD_ANSWERS:
      if (action.payload && isOfType<IResponse>(action.payload, 'answers')) {
        const resultIndex = state.findIndex(
          (result) =>
            result.question.id === (action.payload as IResponse).question.id
        )
        const newResults = [...state]
        newResults[resultIndex].answers = action.payload.answers
        newResults[resultIndex].answered = action.payload.answers.length > 0
        newResults[resultIndex].filtered = true
        return newResults
      } else {
        return state
      }
    case Actions.RESET_RESULTS:
      return initialResponses
    default:
      return state
  }
}

export const resultReducer = (
  state: IResult,
  action: Action<IResult>
): IResult => {
  switch (action.type) {
    case Actions.ADD_RESULT:
      return action.payload ? { ...action.payload } : state
    case Actions.UPDATE_RESULT_HAND:
      return action.payload
        ? { ...state, hand: { ...state.hand, ...action.payload.hand } }
        : state
    default:
      return state
  }
}

export const productsReducer = (
  state: IProductsState,
  action: Action<{ products: IProduct[]; hand: IHand }>
): IProductsState => {
  switch (action.type) {
    case Actions.SET_PRODUCTS:
      return action.payload && action.payload.products
        ? [...action.payload.products]
        : []
    case Actions.FILTER_PRODUCTS:
      // Filter products by selected categories (a.k.a body parts)
      if (state.length && action.payload && action.payload.hand) {
        const categories = action.payload.hand.cards
          .map((card) => card.category?.toLowerCase())
          .filter((x) => x) as string[]

        return !categories.length
          ? // No question categories to filter by (skipped body part questions), show all products
            state
          : state.filter((product) =>
              product.categories?.length
                ? // Filter products by selected card/question categories.
                  categories.filter((x) =>
                    product.categories?.map((c) => c.toLowerCase()).includes(x)
                  ).length >= 1
                : // If product has no categories listed, it is assumed to be applicable to all categories.
                  true
            )
      } else {
        return state
      }
    default:
      return state
  }
}
